import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useCameraStream } from "@/hooks/useCameraStream.js";
import { isHybridDataMode } from "@/config/backendStaging049.js";
import {
  fileToBase64,
  removeOwnAvatar,
  setLocalAvatarPath,
  uploadOwnAvatar,
  validateAvatarFile,
  syncProfileAvatarFromBackend,
} from "@/services/karateAvatarStorage.js";

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSaved: (url: string|null) => void,
 *   personKey: string,
 *   locale: 'fr'|'en',
 *   labels: Record<string, string>,
 *   currentPhotoUrl?: string|null,
 * }} props
 */
export function KarateAvatarPhotoModal({
  open,
  onClose,
  onSaved,
  personKey,
  locale,
  labels,
  currentPhotoUrl = null,
}) {
  const fileRef = useRef(null);
  const { videoRef, starting, error: cameraError, active, start, stop, captureBlob } = useCameraStream();
  const [mode, setMode] = useState("choose");
  const [preview, setPreview] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) {
      setMode("choose");
      setPreview(null);
      setPreviewBlob(null);
      setErr(null);
      stop();
    }
  }, [open, stop]);

  useEffect(() => {
    if (cameraError === "NO_CAMERA" && mode === "camera") {
      setErr(labels.noCamera);
    }
  }, [cameraError, mode, labels.noCamera]);

  async function persistBlob(blob, contentType, source) {
    setBusy(true);
    setErr(null);
    try {
      const file =
        blob instanceof File
          ? blob
          : new File([blob], "profile.jpg", { type: blob.type || contentType || "image/jpeg" });
      const base64 = await fileToBase64(file);
      const savedUrl = await uploadOwnAvatar({
        imageBase64: base64,
        contentType: file.type || contentType,
        source,
        locale,
        labels,
      });
      if (!savedUrl) {
        if (isHybridDataMode()) {
          throw new Error(labels.serverNotConfirmed || labels.saveFailed);
        }
        const previewUrl = URL.createObjectURL(file);
        if (!isHybridDataMode()) setLocalAvatarPath(personKey, previewUrl);
        onSaved(previewUrl);
        onClose();
        return;
      }
      await syncProfileAvatarFromBackend().catch(() => null);
      onSaved(savedUrl);
      onClose();
    } catch (e) {
      setErr(e?.message || labels.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  async function onStartCamera() {
    setErr(null);
    setMode("camera");
    const ok = await start();
    if (!ok) setErr(labels.noCamera);
  }

  async function onPickFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const validationErr = validateAvatarFile(file, labels);
    if (validationErr) {
      setErr(validationErr);
      e.target.value = "";
      return;
    }
    stop();
    setPreviewBlob(file);
    setPreview(URL.createObjectURL(file));
    setMode("preview");
    setErr(null);
    e.target.value = "";
  }

  async function onCapture() {
    setErr(null);
    const blob = await captureBlob();
    if (!blob) {
      setErr(labels.captureFailed);
      return;
    }
    stop();
    setPreviewBlob(blob);
    setPreview(URL.createObjectURL(blob));
    setMode("preview");
  }

  async function onSave() {
    if (!previewBlob) return;
    await persistBlob(previewBlob, previewBlob.type || "image/jpeg", mode === "camera" ? "camera" : "upload");
  }

  async function onRemove() {
    setBusy(true);
    setErr(null);
    try {
      await removeOwnAvatar({ locale });
      setLocalAvatarPath(personKey, null);
      onSaved(null);
      onClose();
    } catch (e) {
      setErr(e?.message || labels.removeFailed);
    } finally {
      setBusy(false);
    }
  }

  function onRecommencer() {
    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    setPreview(null);
    setPreviewBlob(null);
    stop();
    setMode("choose");
    setErr(null);
  }

  if (!open) return null;

  const modal = (
    <div
      className="karate-avatar-modal"
      data-testid="karate-avatar-photo-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="karate-avatar-modal-title"
    >
      <button type="button" className="karate-avatar-modal__backdrop" aria-label={labels.close} onClick={onClose} />
      <div className="karate-avatar-modal__panel" data-testid="karate-avatar-photo-panel">
        <header className="karate-avatar-modal__head">
          <h2 id="karate-avatar-modal-title" className="karate-avatar-modal__title">{labels.profilePhoto}</h2>
          <button type="button" className="karate-avatar-modal__close" onClick={onClose} aria-label={labels.close}>
            ×
          </button>
        </header>

        {currentPhotoUrl && mode === "choose" ? (
          <div className="karate-avatar-modal__current" data-testid="karate-avatar-current-preview">
            <img src={currentPhotoUrl} alt="" className="karate-avatar-modal__current-img" />
          </div>
        ) : null}

        {err ? (
          <p className="karate-avatar-modal__err" role="alert" data-testid="karate-avatar-modal-error">
            {err}
          </p>
        ) : null}

        {mode === "choose" ? (
          <div className="karate-avatar-modal__actions">
            <button
              type="button"
              className="karate-avatar-modal__btn karate-avatar-modal__btn--primary"
              data-testid="karate-avatar-open-camera"
              onClick={onStartCamera}
            >
              {labels.takePhoto}
            </button>
            <button
              type="button"
              className="karate-avatar-modal__btn"
              data-testid="karate-avatar-pick-file"
              onClick={() => fileRef.current?.click()}
            >
              {labels.uploadImage}
            </button>
            {currentPhotoUrl ? (
              <button
                type="button"
                className="karate-avatar-modal__btn karate-avatar-modal__btn--danger"
                data-testid="karate-avatar-remove-photo"
                onClick={onRemove}
                disabled={busy}
              >
                {labels.removePhoto}
              </button>
            ) : null}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              data-testid="karate-avatar-file-input"
              onChange={onPickFile}
            />
          </div>
        ) : null}

        {mode === "camera" ? (
          <div className="karate-avatar-modal__camera" data-testid="karate-avatar-camera-panel">
            <video ref={videoRef} className="karate-avatar-modal__video" playsInline muted />
            <div className="karate-avatar-modal__camera-actions">
              <button type="button" className="karate-avatar-modal__btn" onClick={onRecommencer} disabled={busy}>
                {labels.cancel}
              </button>
              <button
                type="button"
                className="karate-avatar-modal__btn karate-avatar-modal__btn--primary"
                data-testid="karate-avatar-capture"
                onClick={onCapture}
                disabled={!active || starting || busy}
              >
                {labels.capturePhoto}
              </button>
            </div>
          </div>
        ) : null}

        {mode === "preview" ? (
          <div className="karate-avatar-modal__preview" data-testid="karate-avatar-preview-panel">
            <img src={preview} alt="" className="karate-avatar-modal__preview-img" />
            <div className="karate-avatar-modal__camera-actions">
              <button type="button" className="karate-avatar-modal__btn" onClick={onRecommencer} disabled={busy}>
                {labels.cancel}
              </button>
              <button
                type="button"
                className="karate-avatar-modal__btn karate-avatar-modal__btn--primary"
                data-testid="karate-avatar-save"
                onClick={onSave}
                disabled={busy}
              >
                {labels.save}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
